import React, { useState, useMemo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Ionicons from '@react-native-vector-icons/ionicons';
import DropdownMenu from '@/components/General/DropdownMenu';

/**
 * VariableTextInsertButton — a small "variable" button placed next to a text
 * Input that opens a dropdown of available trigger variables. Selecting one
 * appends `{{key}}` to the input's value via onUpdate.
 *
 * Props:
 *  - variables: array of { key, label, type, semantic? } (already filtered)
 *  - value: current text value
 *  - onUpdate: callback that receives the new text
 *  - testID: optional testID for the button
 *
 * Note: inserts at end of text (not at cursor) for cross-platform reliability.
 * Inline `{{` autocomplete was considered but skipped due to platform divergence
 * in TextInput cursor APIs (web vs native).
 */
const VariableTextInsertButton = ({
    variables = [],
    value,
    onUpdate,
    testID = 'variable-insert-button',
}) => {
    const [triggerLayout, setTriggerLayout] = useState(null);
    const { theme: { colors } } = useUnistyles();

    const options = useMemo(() =>
        variables.map(v => ({
            name: v.label,
            icon: { web: 'flash' },
            onSelect: () => {
                const token = `{{${v.key}}}`;
                const current = value || '';
                // Insert at end with a leading space if needed
                const needsSpace = current.length > 0 && !current.endsWith(' ');
                onUpdate(current + (needsSpace ? ' ' : '') + token);
            },
        })),
    [variables, value, onUpdate]);

    if (!variables.length) return null;

    return (
        <View
            onLayout={(e) => setTriggerLayout(e.nativeEvent.layout)}
        >
            <DropdownMenu
                testID={testID}
                options={options}
                showSearch
                dropdownWidth={Math.max(triggerLayout?.width, 280)}
                triggerComponent={
                    <Pressable
                        testID={testID}
                        style={({ hovered, pressed }) => [styles.button, (hovered || pressed) && styles.buttonHover]}
                    >
                        <Ionicons name="flash" size={14} color={colors.primary} />
                        <Text style={styles.buttonText}>Insert Variable</Text>
                    </Pressable>
                }
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingVertical: 6,
        paddingHorizontal: 8,
        borderRadius: 6,
        marginTop: 6,
        alignSelf: 'flex-start',
    },
    buttonHover: {
        backgroundColor: colors.primaryContainer,
    },
    buttonText: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.primary,
    },
}));

export default VariableTextInsertButton;
